import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, ShieldX, Trash2 } from "lucide-react";
import api from "@/lib/api";

const PAGE_SIZE = 20;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Country code → flag emoji via Unicode regional indicators. */
function countryFlag(code) {
  if (!code || code.length !== 2) return "";
  const base = 0x1f1e6 - 65;
  return (
    String.fromCodePoint(base + code.toUpperCase().charCodeAt(0)) +
    String.fromCodePoint(base + code.toUpperCase().charCodeAt(1))
  );
}

/** Humanize a URL path into a readable page label.
 *  /                         → Home
 *  /books                    → Books
 *  /journal                  → Journal
 *  /journal/a-new-chapter    → Journal · A New Chapter
 *  /contact                  → Contact
 */
function humanizePath(path) {
  if (!path || path === "/") return "Home";
  const parts = path.replace(/^\//, "").split("/");
  const section = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  if (parts.length === 1) return section;
  const slug = parts
    .slice(1)
    .join("/")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${section} · ${slug}`;
}

/** Format a duration in seconds as a compact "3m 12s" / "45s" / "1h 4m" label. */
function formatDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

/** Single visit row — shows all 4 data points:
 *  1. Flag + full IP-based location  (city · region · country)
 *  2. Page navigated to              (humanized path)
 *  3. Device                         (browser · OS)
 *  4. IP address                     (last octet masked)
 */
function VisitRow({ r, i }) {
  const flag = countryFlag(r.country_code);
  const suspicious = !!r.suspicious;

  // Full geo string — city, region, country (dedup consecutive identical parts)
  const locationParts = [r.city, r.region, r.country].filter(Boolean);
  const location = locationParts.filter((v, idx) => v !== locationParts[idx - 1]).join(", ");
  const hasGeo = !!location;

  // Device — drop any generic "Unknown x" fallback labels
  const deviceParts = [r.browser, r.os].filter((v) => v && !v.startsWith("Unknown"));

  if (suspicious) {
    return (
      <li
        className="py-3 px-3 -mx-3 bg-red-50/70 border-l-2 border-red-400"
        data-testid={`recent-visit-${i}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-red-700" data-testid={`visit-suspicious-${i}`}>
              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span>Suspicious probe</span>
              <span className="text-[0.6rem] tracking-[0.18em] uppercase bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                {r.block_reason === "malicious_ua" ? "attack tool" : "bogus path"} · blocked
              </span>
            </div>
            <div className="text-xs text-[#5C6670] space-y-0.5">
              <div>
                <span className="text-[#B8BCC2]">Probed </span>
                <span className="font-mono text-red-600 break-all" data-testid={`visit-path-${i}`}>{r.path}</span>
              </div>
              <div>
                {flag && <span className="mr-1">{flag}</span>}
                {hasGeo ? location : "Unknown location"}
                {" · "}
                <span className="font-mono text-[#1A1F24]" data-testid={`visit-ip-${i}`}>{r.ip || r.ip_masked}</span>
              </div>
              {r.user_agent && (
                <div className="text-[#9C9C94] break-all" data-testid={`visit-ua-${i}`}>
                  <span className="text-[#B8BCC2]">UA: </span>{r.user_agent}
                </div>
              )}
            </div>
          </div>
          <span className="text-xs text-[#B8BCC2] whitespace-nowrap shrink-0 pt-0.5">
            {formatDate(r.created_at)}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li
      className="py-3 flex items-start justify-between gap-3"
      data-testid={`recent-visit-${i}`}
    >
      <div className="min-w-0 flex-1 space-y-1">

        {/* ── Line 1: IP-based location ── */}
        <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A1F24]">
          {hasGeo ? (
            <>
              {flag && <span className="shrink-0">{flag}</span>}
              <span className="truncate">{location}</span>
            </>
          ) : r.ip_masked ? (
            <span className="font-mono text-xs text-[#5C6670]" title="IP-based location unresolved">
              {r.ip_masked}
            </span>
          ) : (
            <span className="text-[#B8BCC2] italic text-xs">Unknown location</span>
          )}
        </div>

        {/* ── Line 2: Navigation · Device · IP ── */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">

          {/* Page visited — full journey when available, otherwise prev → current */}
          <span>
            <span className="text-[#B8BCC2]">Visited </span>
            {Array.isArray(r.visited_paths) && r.visited_paths.length > 1 ? (
              <span data-testid={`visit-journey-${i}`}>
                {r.visited_paths.map((p, idx) => {
                  const isLast = idx === r.visited_paths.length - 1;
                  return (
                    <span key={`${p}-${idx}`}>
                      <span
                        className={isLast ? "text-[#1A1F24] font-medium" : "text-[#9C9C94]"}
                        data-testid={isLast ? `visit-path-${i}` : `visit-hop-${i}-${idx}`}
                      >
                        {humanizePath(p)}
                      </span>
                      {!isLast && <span className="mx-1 text-[#C5A059]">→</span>}
                    </span>
                  );
                })}
              </span>
            ) : r.previous_path && r.previous_path !== r.path ? (
              <>
                <span className="text-[#9C9C94]" data-testid={`visit-prev-path-${i}`}>
                  {humanizePath(r.previous_path)}
                </span>
                <span className="mx-1 text-[#C5A059]">→</span>
                <span className="text-[#1A1F24] font-medium" data-testid={`visit-path-${i}`}>
                  {humanizePath(r.path)}
                </span>
              </>
            ) : (
              <span className="text-[#1A1F24] font-medium" data-testid={`visit-path-${i}`}>
                {humanizePath(r.path)}
              </span>
            )}
          </span>

          {/* Traffic source — where they arrived from (Google, Twitter, Direct…) */}
          {r.source && (
            <>
              <span className="text-[#D8D8D0]">·</span>
              <span
                className="text-[#5C6670]"
                data-testid={`visit-source-${i}`}
                title={r.referrer_host || undefined}
              >
                via <span className="text-[#C5A059]">{r.source}</span>
              </span>
            </>
          )}

          {/* Session duration — only when we've observed >=1 navigation */}
          {r.duration_seconds > 0 && (
            <>
              <span className="text-[#D8D8D0]">·</span>
              <span className="text-[#5C6670]" data-testid={`visit-duration-${i}`}>
                Stayed {formatDuration(r.duration_seconds)}
              </span>
            </>
          )}

          {/* Device */}
          {deviceParts.length > 0 && (
            <>
              <span className="text-[#D8D8D0]">·</span>
              <span className="text-[#5C6670]">{deviceParts.join(" · ")}</span>
            </>
          )}

          {/* Masked IP — always show when available */}
          {r.ip_masked && (
            <>
              <span className="text-[#D8D8D0]">·</span>
              <span className="font-mono text-[#9C9C94]" data-testid={`visit-ip-${i}`}>
                {r.ip_masked}
              </span>
            </>
          )}

        </div>
      </div>

      <span className="text-xs text-[#B8BCC2] whitespace-nowrap shrink-0 pt-0.5">
        {formatDate(r.created_at)}
      </span>
    </li>
  );
}

// Auto-blocked IPs — hostile visitors that hit a probe path or attack-tool
// signature and were blocked site-wide. Admin can lift a block (false positive).
function BlockedIpsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/stats/admin/blocked")
      .then(({ data }) => setItems(data))
      .catch((err) => console.error("Failed to load blocked IPs:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function unblock(ip) {
    api
      .delete(`/stats/admin/blocked/${encodeURIComponent(ip)}`)
      .then(() => setItems((prev) => prev.filter((x) => x.ip !== ip)))
      .catch((err) => console.error("Failed to unblock:", err));
  }

  if (loading || items.length === 0) return null;

  return (
    <div className="mb-6 border border-red-200 bg-red-50/60 rounded" data-testid="blocked-ips-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
        data-testid="blocked-ips-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-red-700">
          <ShieldX className="w-4 h-4" aria-hidden="true" />
          {items.length} auto-blocked {items.length === 1 ? "attacker" : "attackers"}
        </span>
        <span className="text-xs text-red-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ul className="divide-y divide-red-100 px-3 pb-2 max-h-72 overflow-y-auto" data-testid="blocked-ips-list">
          {items.map((b, idx) => (
            <li key={b.ip} className="py-2 flex items-start justify-between gap-3 text-xs" data-testid={`blocked-ip-${idx}`}>
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="font-mono text-[#1A1F24]">{b.ip}
                  <span className="ml-2 text-[0.6rem] tracking-[0.16em] uppercase bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                    {b.reason === "malicious_ua" ? "attack tool" : "bogus path"}
                  </span>
                  <span className="ml-2 text-[#9C9C94]">{b.hits} hit{b.hits !== 1 ? "s" : ""}</span>
                </div>
                {b.last_path && <div className="font-mono text-red-600 break-all">{b.last_path}</div>}
                {b.user_agent && <div className="text-[#9C9C94] break-all">{b.user_agent}</div>}
              </div>
              <button
                type="button"
                onClick={() => unblock(b.ip)}
                className="shrink-0 inline-flex items-center gap-1 text-[#5C6670] hover:text-red-600 transition-colors"
                data-testid={`unblock-ip-${idx}`}
                title="Lift this block"
              >
                <Trash2 className="w-3.5 h-3.5" /> Unblock
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Paginated, full-history visit log — admin can page back through every recorded
// visit since 30 June 2026.
export function RecentVisitsList({ totalVisits = 0, uniqueVisits = 0 }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  const loadPage = useCallback((p) => {
    setLoading(true);
    api
      .get("/stats/admin/visits", { params: { page: p, page_size: PAGE_SIZE } })
      .then(({ data }) => setData(data))
      .catch((err) => console.error("Failed to load recent visits:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPage(page); }, [page, loadPage]);

  function handleEnrich() {
    setEnriching(true);
    setEnrichResult(null);
    api
      .post("/stats/admin/enrich-visits")
      .then(({ data }) => { setEnrichResult(data); loadPage(page); })
      .catch(() => setEnrichResult({ error: true }))
      .finally(() => setEnriching(false));
  }

  const unknownCount = data.items.filter((r) => !r.country && !r.city).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">
            Recent visits · since 30 June 2026
          </div>
          <div className="flex items-baseline gap-5 mt-1">
            <div>
              <span className="font-serif text-3xl text-[#1A1F24]" data-testid="visits-total-count">
                {(totalVisits || 0).toLocaleString()}
              </span>
              <span className="text-[0.6rem] tracking-[0.22em] uppercase text-[#5C6670] ml-2">total visits</span>
            </div>
            {uniqueVisits > 0 && (
              <div>
                <span className="font-serif text-3xl text-[#C5A059]" data-testid="visits-unique-count">
                  {uniqueVisits.toLocaleString()}
                </span>
                <span className="text-[0.6rem] tracking-[0.22em] uppercase text-[#5C6670] ml-2">unique</span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleEnrich}
          disabled={enriching}
          data-testid="enrich-visits-btn"
          className="text-[0.6rem] tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059] disabled:opacity-40 transition-colors border border-[#E5E5E5] hover:border-[#C5A059] px-2 py-1"
          title="Re-resolve region and browser info for unknown visitors"
        >
          {enriching ? "Resolving…" : "Resolve Unknowns"}
        </button>
      </div>

      <BlockedIpsPanel />

      {/* Enrichment result */}
      {enrichResult && !enrichResult.error && (
        <div data-testid="enrich-result" className="mb-3 text-xs text-[#5C6670] bg-[#FAFAF8] border border-[#E5E5E5] px-3 py-2">
          {enrichResult.geo_updated === 0 && enrichResult.nav_updated === 0 ? (
            <span>All visitors already resolved — nothing to update.</span>
          ) : (
            <span>
              {enrichResult.geo_updated > 0 && (
                <span className="text-[#C5A059] font-medium">
                  {enrichResult.geo_updated} region{enrichResult.geo_updated !== 1 ? "s" : ""} resolved
                </span>
              )}
              {enrichResult.geo_updated > 0 && enrichResult.nav_updated > 0 && <span> · </span>}
              {enrichResult.nav_updated > 0 && (
                <span>{enrichResult.nav_updated} visitor{enrichResult.nav_updated !== 1 ? "s" : ""} updated</span>
              )}
            </span>
          )}
        </div>
      )}
      {enrichResult?.error && (
        <div data-testid="enrich-error" className="mb-3 text-xs text-red-500">Failed to resolve — please try again.</div>
      )}

      {/* Visit rows */}
      {data.items.length === 0 && !loading ? (
        <p className="text-sm text-[#5C6670] italic" data-testid="no-recent">No visits recorded yet.</p>
      ) : (
        <ul className="divide-y divide-[#F0F0F0] max-h-[30rem] overflow-y-auto pr-1" data-testid="recent-visits-list">
          {data.items.map((r, i) => (
            <VisitRow key={`${r.created_at}-${i}`} r={r} i={i} />
          ))}
        </ul>
      )}

      {unknownCount > 0 && (
        <p className="mt-2 text-xs text-[#B8BCC2] italic" data-testid="unknown-count-hint">
          {unknownCount} visitor{unknownCount !== 1 ? "s" : ""} on this page have no resolved region — click &quot;Resolve Unknowns&quot; to re-attempt.
        </p>
      )}

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-between pt-3 mt-2 border-t border-[#F0F0F0] text-xs" data-testid="recent-visits-pagination">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#5C6670]"
            data-testid="recent-visits-page-prev"
          >
            ← Newer
          </button>
          <span className="tracking-[0.2em] uppercase text-[#B8BCC2]" data-testid="recent-visits-page-indicator">
            Page {page} of {data.total_pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
            disabled={page >= data.total_pages}
            className="tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#5C6670]"
            data-testid="recent-visits-page-next"
          >
            Older →
          </button>
        </div>
      )}
    </div>
  );
}
